import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ScenarioRunDocument, ScenarioRunEvent, ScenarioRunScenario } from '../../../api/api';
import { runTime } from '../../../utils/scenarioRunFormat';
import { durationMs, formatDuration } from './runDetailModel';
import {
    documentAmount,
    documentIsDone,
    FlowRow,
    Lane,
    nowTitle,
    scenarioFlow,
    stepAt,
    stageLook,
    stationsOf,
    WayState,
} from './scenarioFlowModel';
import { OPEN_WAY_STATES } from './followModel';
import { OPEN_ROW } from './useFollowRun';
import './ScenarioFlow.scss';

const LANES: { lane: Lane; label: string }[] = [
    { lane: 'chargebee', label: 'Chargebee' },
    { lane: 'connector', label: 'Connector' },
    { lane: 'exact', label: 'Exact' },
];

/** The current time, every second while something is going, so the durations on the page count along. */
const useNow = (ticking: boolean) => {
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        if (!ticking) {
            return undefined;
        }

        const timer = setInterval(() => setNow(Date.now()), 1000);

        return () => clearInterval(timer);
    }, [ticking]);

    return now;
};

const at = (moment: string | undefined, now: number) => (moment ? new Date(moment).getTime() : now);

/**
 * What a scenario did and where its documents are, drawn rather than logged: on top each document on its way from
 * Chargebee to Exact, below that every step and every stage in the lane of the system it happened in - the same
 * lanes as the dossier's flow - and each document's way through both connector apps to where it ends up, with a line
 * along it. Each card says itself whether it expects the document, works on it, holds it or is through with it.
 */
export const ScenarioFlow = ({
    scenario,
    events,
    running,
}: {
    scenario: ScenarioRunScenario;
    events: ScenarioRunEvent[];
    running: boolean;
}) => {
    const now = useNow(running);
    const rows = useMemo(() => scenarioFlow(scenario, events, running), [scenario, events, running]);
    const documents = scenario.documents ?? [];
    const start = scenario.startedAtUtc;

    if (rows.length === 0 && documents.length === 0) {
        return null;
    }

    return (
        <div className="rd-part sf">
            <h4 className="rd-part__title">What happened</h4>

            {documents.length > 0 && (
                <DocumentStrip
                    documents={documents}
                    end={running ? now : at(scenario.finishedAtUtc, now)}
                />
            )}

            <div
                className="sf-flow"
                role="table"
                aria-label="What the scenario did in Chargebee, the connector and Exact"
            >
                <FlowLinks rows={rows} />

                <div className="sf-row sf-row--head" role="row">
                    <span role="columnheader">When</span>
                    {LANES.map(({ lane, label }) => (
                        <span key={lane} role="columnheader" className={`sf-lane-head sf-lane-head--${lane}`}>
                            {lane === 'connector' && <FontAwesomeIcon icon="robot" />} {label}
                        </span>
                    ))}
                </div>

                {rows.map((row, index) => (
                    <Row key={index} row={row} start={start} now={now} running={running} />
                ))}

                {running && <NowRow scenario={scenario} events={events} documents={documents} now={now} />}
            </div>
        </div>
    );
};

/**
 * Each followed document as four stations, the one it stands at marked, and for how long: an invoice from Chargebee
 * to Exact, a payment made in Exact the other way.
 */
const DocumentStrip = ({ documents, end }: { documents: ScenarioRunDocument[]; end: number }) => (
    <ul className="sf-docs" aria-label="Where each document is">
        {documents.map((document) => {
            const look = stageLook(document.stage);
            const since = durationMs(document.sinceUtc, new Date(end).toISOString());
            const amount = documentAmount(document);

            return (
                <li key={document.id} className={`sf-doc sf-doc--${look.tone}`} title={look.hint}>
                    <span className="sf-doc__name">
                        {document.name}
                        {amount && <span className="sf-amount">{amount}</span>}
                        <code className="sf-doc__id">{document.id}</code>
                    </span>

                    <ol className="sf-doc__stations">
                        {stationsOf(document.stage).map((label, station) => (
                            <li
                                key={label}
                                className={`sf-doc__station sf-doc__station--${
                                    station < look.station ? 'passed' : station === look.station ? 'at' : 'ahead'
                                }`}
                            >
                                <span className="sf-doc__dot">
                                    {station === look.station && <FontAwesomeIcon icon={look.icon} />}
                                </span>
                                <span className="sf-doc__label">{label}</span>
                            </li>
                        ))}
                    </ol>

                    <span className="sf-doc__state">
                        <span className="sf-doc__stage">{look.title}</span>
                        {document.detail && (
                            <span className="sf-doc__detail" title={document.detail}>
                                {document.detail}
                            </span>
                        )}
                        {!documentIsDone(document) && since > 0 && (
                            <span className="sf-doc__since">for {formatDuration(since)}</span>
                        )}
                    </span>
                </li>
            );
        })}
    </ul>
);

/** One line of a document's way: from the bottom of one card, down and across to the side of the next. */
interface LinkPath {
    d: string;
    expected: boolean;
    /** Where the line leaves its card: marked with a dot, so a line that only passes behind a card has none there. */
    start: [number, number];
}

// How round a line turns its corner, and how far apart the lines of two documents run, in pixels. A line in the gap
// between two lanes runs at the lanes' border plus its document's offset: with 20 px of padding a side, three
// documents' lines fit side by side and each keeps a straight stretch before its arrowhead.
const CORNER = 6;
const TRACK = 8;

interface Box {
    left: number;
    right: number;
    top: number;
    bottom: number;
}

// A straight piece of a line, across or down.
interface Segment {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
}

// Whether a piece of line runs through a box: then the box hides it, and the line seems to go somewhere else.
const crosses = (segment: Segment, box: Box) => {
    const inset = 2;
    const [x1, x2] = [Math.min(segment.x1, segment.x2), Math.max(segment.x1, segment.x2)];
    const [y1, y2] = [Math.min(segment.y1, segment.y2), Math.max(segment.y1, segment.y2)];

    return x2 > box.left + inset && x1 < box.right - inset && y2 > box.top + inset && y1 < box.bottom - inset;
};

// A path through the given points, turning each corner in a short curve.
const pathThrough = (points: [number, number][]) => {
    let d = `M ${points[0][0]} ${points[0][1]}`;

    for (let index = 1; index < points.length - 1; index++) {
        const [px, py] = points[index - 1];
        const [x, y] = points[index];
        const [nx, ny] = points[index + 1];
        const into = Math.min(CORNER, Math.hypot(x - px, y - py) / 2);
        const out = Math.min(CORNER, Math.hypot(nx - x, ny - y) / 2);
        const before: [number, number] = [x - Math.sign(x - px) * into, y - Math.sign(y - py) * into];
        const after: [number, number] = [x + Math.sign(nx - x) * out, y + Math.sign(ny - y) * out];

        d += ` L ${before[0]} ${before[1]} Q ${x} ${y} ${after[0]} ${after[1]}`;
    }

    const [lx, ly] = points[points.length - 1];

    return `${d} L ${lx} ${ly}`;
};

// The straight pieces between the points of a route, to check what they run through.
const segmentsOf = (points: [number, number][]): Segment[] =>
    points.slice(1).map(([x2, y2], index) => ({ x1: points[index][0], y1: points[index][1], x2, y2 }));

/**
 * Measures the cards a document passes and draws the line between each and the next. Measured, not laid out in the
 * grid: the cards of one document can be rows apart, and only the browser knows where each one ended up.
 *
 * Between two lanes a line leaves its card from the side, runs down the gap between the lanes on its document's own
 * track, and goes into the side of the next card; within a lane it goes straight down, or round the side when a card
 * stands in between. Whichever way runs through fewer other cards wins: a line that disappears behind a card seems
 * to lead to that card.
 */
export const linkPaths = (container: HTMLElement): LinkPath[] => {
    const origin = container.getBoundingClientRect();
    const boxOf = (element: Element): Box => {
        const rect = element.getBoundingClientRect();

        return {
            left: rect.left - origin.left,
            right: rect.right - origin.left,
            top: rect.top - origin.top,
            bottom: rect.bottom - origin.top,
        };
    };

    // Where each lane begins, from the heading, and where the last one ends.
    const borders = [
        ...(['chargebee', 'connector', 'exact'] as const).map(
            (lane) => (container.querySelector(`.sf-lane-head--${lane}`)?.getBoundingClientRect().left ?? origin.left) - origin.left
        ),
        origin.width,
    ];
    const laneOf = (box: Box) => {
        const middle = (box.left + box.right) / 2;

        return middle >= borders[2] ? 2 : middle >= borders[1] ? 1 : 0;
    };

    const ways = new Map<string, HTMLElement[]>();

    container.querySelectorAll<HTMLElement>('[data-links]').forEach((card) => {
        for (const document of (card.dataset.links ?? '').split(' ').filter(Boolean)) {
            ways.set(document, [...(ways.get(document) ?? []), card]);
        }
    });

    const obstacles = Array.from(
        container.querySelectorAll('.sf-card, .sf-note, .sf-problem, .sf-verdict, .sf-phase__label')
    );
    const paths: LinkPath[] = [];
    let track = 0;

    ways.forEach((cards) => {
        // Each document its own track: the same offset from the middle of a gap, or of a card, wherever its line runs.
        const offset = (track++ - (ways.size - 1) / 2) * TRACK;
        // The side the last line went into its card: the next line leaves from another, or the two would overlap.
        let entered: 'left' | 'right' | 'top' | undefined;

        for (let index = 1; index < cards.length; index++) {
            const from = boxOf(cards[index - 1]);
            const to = boxOf(cards[index]);
            const others = obstacles
                .filter((element) => element !== cards[index - 1] && element !== cards[index])
                .map(boxOf);
            const hits = (points: [number, number][]) =>
                others.filter((box) => segmentsOf(points).some((segment) => crosses(segment, box))).length;
            const expected = cards[index].dataset.expected === 'true';

            const fromLane = laneOf(from);
            const toLane = laneOf(to);
            const fromMiddle = (from.top + from.bottom) / 2;
            const toMiddle = (to.top + to.bottom) / 2;
            const routes: [number, number][][] = [];

            if (fromLane === toLane) {
                const x = (from.left + from.right) / 2 + offset;

                // Straight down onto the next card.
                routes.push([
                    [x, from.bottom],
                    [x, to.top],
                ]);

                // Or round the side, down the gap beside the lane, past whatever stands between the two. Not past the
                // outer edge of the flow: Chargebee's lane only has a gap on its right, Exact's only on its left.
                const sides = [...(fromLane < 2 ? ['right' as const] : []), ...(fromLane > 0 ? ['left' as const] : [])];

                for (const side of sides) {
                    const gap = (side === 'right' ? borders[fromLane + 1] : borders[fromLane]) + offset;
                    const out = side === 'right' ? from.right : from.left;
                    const into = side === 'right' ? to.right : to.left;

                    routes.push([
                        [out, fromMiddle],
                        [gap, fromMiddle],
                        [gap, toMiddle],
                        [into, toMiddle],
                    ]);
                }
            } else {
                const towardsRight = toLane > fromLane;
                // The gap on the side the next card is entered from.
                const gap = (towardsRight ? borders[toLane] : borders[toLane + 1]) + offset;
                const out = towardsRight ? from.right : from.left;
                const into = towardsRight ? to.left : to.right;

                // Out of the side, down the gap between the lanes, into the side of the next.
                routes.push([
                    [out, fromMiddle],
                    [gap, fromMiddle],
                    [gap, toMiddle],
                    [into, toMiddle],
                ]);

                // Down from under the card, then across into the side of the next.
                const x = (from.left + from.right) / 2 + offset;

                routes.push([
                    [x, from.bottom],
                    [x, toMiddle],
                    [into, toMiddle],
                ]);
            }

            const leaves = (route: [number, number][]) =>
                route[0][0] === from.right ? 'right' : route[0][0] === from.left ? 'left' : 'bottom';
            const enteredHere = entered;
            const cost = (route: [number, number][]) => hits(route) + (leaves(route) === enteredHere ? 0.5 : 0);

            // The fewest cards in the way; on a tie the first.
            const best = routes.reduce((chosen, route) => (cost(route) < cost(chosen) ? route : chosen));
            const [endX] = best[best.length - 1];

            entered = endX === to.left ? 'left' : endX === to.right ? 'right' : 'top';
            paths.push({ d: pathThrough(best), expected, start: best[0] });
        }
    });

    return paths;
};

/**
 * The lines along each document's way, drawn under the cards and over the lanes. It measures the flow it sits in from
 * its own element: a parent's ref is only set after its children's layout effects ran.
 */
const FlowLinks = ({ rows }: { rows: FlowRow[] }) => {
    const [paths, setPaths] = useState<LinkPath[]>([]);
    const svg = useRef<SVGSVGElement>(null);

    useLayoutEffect(() => {
        const element = svg.current?.parentElement;

        if (!element) {
            return undefined;
        }

        const measure = () => {
            const next = linkPaths(element);

            setPaths((previous) => (JSON.stringify(previous) === JSON.stringify(next) ? previous : next));
        };

        measure();

        if (typeof ResizeObserver === 'undefined') {
            return undefined;
        }

        const observer = new ResizeObserver(measure);

        observer.observe(element);

        return () => observer.disconnect();
    }, [rows]);

    return (
        <>
            <svg className="sf-links" aria-hidden ref={svg}>
                <defs>
                    <marker id="sf-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto">
                        <path d="M 0 0 L 10 5 L 0 10 z" className="sf-links__head" />
                    </marker>
                    <marker
                        id="sf-arrow-expected"
                        viewBox="0 0 10 10"
                        refX="9"
                        refY="5"
                        markerWidth="7"
                        markerHeight="7"
                        orient="auto"
                    >
                        <path d="M 0 0 L 10 5 L 0 10 z" className="sf-links__head sf-links__head--expected" />
                    </marker>
                </defs>
                {paths.map((path, index) => (
                    <path
                        key={index}
                        d={path.d}
                        className={`sf-links__line${path.expected ? ' sf-links__line--expected' : ''}`}
                        markerEnd={`url(#${path.expected ? 'sf-arrow-expected' : 'sf-arrow'})`}
                    />
                ))}
            </svg>
            {/*
                A dot where each line leaves its card, over the cards: under them, half of it would hide behind the card it
                belongs to. The arrowhead marks the other end, and a line that only passes behind a card has neither there.
            */}
            <svg className="sf-links sf-links--joints" aria-hidden>
                {paths.map((path, index) => (
                    <circle
                        key={index}
                        cx={path.start[0]}
                        cy={path.start[1]}
                        r={3.25}
                        className={`sf-links__joint${path.expected ? ' sf-links__joint--expected' : ''}`}
                    />
                ))}
            </svg>
        </>
    );
};

/** A step's own lines: the last one, which is where the step ended up, and the rest behind a toggle. */
const StepNotes = ({ notes }: { notes: string[] }) => {
    if (notes.length === 0) {
        return null;
    }

    const last = notes[notes.length - 1];
    const before = notes.slice(0, -1);

    return (
        <>
            <span className="sf-card__meta" title={last}>
                {last}
            </span>
            {before.length > 0 && (
                <details className="sf-card__more">
                    <summary>
                        {before.length} earlier line{before.length > 1 ? 's' : ''}
                    </summary>
                    {before.map((note, index) => (
                        <span key={index} className="sf-card__meta" title={note}>
                            {note}
                        </span>
                    ))}
                </details>
            )}
        </>
    );
};

const When = ({ moment, start }: { moment: string; start?: string }) => (
    <span className="sf-when" role="cell">
        {runTime(moment)}
        {start && <span className="sf-when__offset">+{formatDuration(durationMs(start, moment))}</span>}
    </span>
);

/** The connector app a card is about, by the short name everyone uses for it. */
const AppTag = ({ app }: { app: string }) => <span className="sf-app-tag">{app}</span>;

// The mark of each state on a card of a document's way.
const WAY_ICON: Record<WayState, IconProp> = {
    expected: 'circle',
    busy: 'spinner',
    waiting: 'hourglass-half',
    done: 'circle-check',
    retrying: 'rotate',
    failed: 'circle-xmark',
    bypassed: 'ban',
    there: 'circle-check',
    none: 'minus',
};

/**
 * A part of a document's way: in the connector's lane one of its apps, by name, with what it is doing; in Chargebee's
 * or Exact's lane where the document has to end up. Dimmed while it has not happened yet.
 */
const WayCard = ({ row, running, now }: { row: Extract<FlowRow, { kind: 'way' }>; running: boolean; now: number }) => {
    // How long the app took, or is taking: the clock time is in the When column already.
    const took =
        row.tookMs ?? (running && row.sinceUtc ? Math.max(now - new Date(row.sinceUtc).getTime(), 0) : undefined);

    return (
        <span
            className={`sf-card sf-card--way sf-card--way-${row.state}${row.part === 'target' ? ' sf-card--target' : ''}`}
            data-links={row.link.document}
            data-expected={row.link.expected ? 'true' : undefined}
        >
            {row.app ? (
                <AppTag app={row.app} />
            ) : (
                <FontAwesomeIcon icon={row.lane === 'exact' ? 'building-columns' : 'file-invoice'} className="sf-card__icon" />
            )}
            <span className="sf-card__text">
                <span className="sf-card__title">
                    {row.name}
                    {row.amount && <span className="sf-amount">{row.amount}</span>}
                </span>
                <span className="sf-way-state">
                    <FontAwesomeIcon icon={WAY_ICON[row.state]} spin={row.state === 'busy' && running} />
                    <span>
                        {row.text}
                        {took !== undefined && took >= 1000 && (
                            <span className="sf-way-state__took"> · {formatDuration(took)}</span>
                        )}
                    </span>
                </span>
                <span className="sf-card__meta" title={row.fullDetail ?? row.detail}>
                    <code>{row.id}</code>
                    {row.detail && ` · ${row.detail}`}
                </span>
            </span>
        </span>
    );
};

/** Puts something in its lane and leaves the other two empty, so the eye finds a system in the same place each row. */
const InLane = ({ lane, children }: { lane: Lane; children: React.ReactNode }) => (
    <>
        {LANES.map((candidate) => (
            <span key={candidate.lane} role="cell" className={`sf-cell sf-cell--${candidate.lane}`}>
                {candidate.lane === lane && children}
            </span>
        ))}
    </>
);

const Row = ({ row, start, now, running }: { row: FlowRow; start?: string; now: number; running: boolean }) => {
    switch (row.kind) {
        case 'phase': {
            const going = !row.phase.finishedAtUtc;

            return (
                <div
                    className="sf-row sf-row--phase"
                    role="row"
                    title={row.phase.title}
                >
                    <When moment={row.at} start={start} />
                    <span role="cell" className="sf-cell sf-cell--span sf-phase">
                        <span className={`rd-phases__swatch rd-phases__swatch--${row.phase.kind}`} />
                        <span className="sf-phase__label">{row.phase.name}</span>
                        <span className="sf-phase__time">
                            {formatDuration(
                                durationMs(row.phase.startedAtUtc, row.phase.finishedAtUtc ?? new Date(now).toISOString())
                            )}
                            {going && '…'}
                        </span>
                    </span>
                </div>
            );
        }

        case 'step':
            return (
                <div className={`sf-row${running && row.going ? ` ${OPEN_ROW}` : ''}`} role="row">
                    <When moment={row.at} start={start} />
                    <InLane lane={row.lane}>
                        <span
                            className="sf-card sf-card--step"
                            data-links={row.links.map((link) => link.document).join(' ') || undefined}
                        >
                            <FontAwesomeIcon icon={row.icon} className="sf-card__icon" />
                            <span className="sf-card__text">
                                <span className="sf-card__title">
                                    {row.app && <AppTag app={row.app} />}
                                    {row.title}
                                </span>
                                <StepNotes notes={row.notes} />
                            </span>
                        </span>
                    </InLane>
                </div>
            );

        case 'way':
            return (
                <div
                    className={`sf-row${running && OPEN_WAY_STATES.includes(row.state) ? ` ${OPEN_ROW}` : ''}`}
                    role="row"
                >
                    {row.at ? (
                        <When moment={row.at} start={start} />
                    ) : (
                        <span className="sf-when sf-when--next" role="cell">
                            {running ? 'Next' : 'Not reached'}
                        </span>
                    )}
                    <InLane lane={row.lane}>
                        <WayCard row={row} running={running} now={now} />
                    </InLane>
                </div>
            );

        case 'note':
            return (
                <div className="sf-row sf-row--note" role="row">
                    <When moment={row.at} start={start} />
                    <InLane lane={row.lane}>
                        <span className="sf-note" title={row.text}>
                            {row.text}
                        </span>
                    </InLane>
                </div>
            );

        case 'problem':
            return (
                <div className="sf-row" role="row">
                    <When moment={row.at} start={start} />
                    <span role="cell" className="sf-cell sf-cell--span">
                        <span className={`sf-problem sf-problem--${row.level}`}>
                            <FontAwesomeIcon icon={row.level === 'error' ? 'circle-xmark' : 'triangle-exclamation'} />
                            <span>{row.text}</span>
                        </span>
                    </span>
                </div>
            );

        case 'checks': {
            const held = row.failed.length === 0;

            return (
                <div className="sf-row" role="row">
                    <When moment={row.at} start={start} />
                    <span role="cell" className="sf-cell sf-cell--span">
                        <span className={`sf-verdict sf-verdict--${held ? 'ok' : 'error'}`}>
                            <FontAwesomeIcon icon={held ? 'circle-check' : 'circle-xmark'} />
                            <span>
                                {row.passed} of {row.total} checks held
                                {!held &&
                                    row.failed.map((line) => (
                                        <span key={line} className="sf-verdict__failed">
                                            {line}
                                        </span>
                                    ))}
                            </span>
                        </span>
                    </span>
                </div>
            );
        }

        default:
            return null;
    }
};

/** What the scenario is doing this moment, and until when it waits; the cards above say on what. */
const NowRow = ({
    scenario,
    events,
    documents,
    now,
}: {
    scenario: ScenarioRunScenario;
    events: ScenarioRunEvent[];
    documents: ScenarioRunDocument[];
    now: number;
}) => {
    const open = documents.filter((document) => !documentIsDone(document));
    const until = scenario.waitingUntilUtc ? new Date(scenario.waitingUntilUtc).getTime() : undefined;
    const left = until !== undefined ? Math.max(until - now, 0) : undefined;

    return (
        <div className={`sf-row sf-row--now ${OPEN_ROW}`} role="row">
            <span className="sf-when sf-when--now" role="cell">
                now
            </span>
            <span role="cell" className="sf-cell sf-cell--span">
                <span className="sf-now">
                    <span className="sf-now__head">
                        <span className="sf-now__what" title={scenario.step}>
                            <FontAwesomeIcon icon="spinner" spin />{' '}
                            {nowTitle(stepAt(scenario.step, events, now), open.length > 0 && until !== undefined)}
                        </span>
                        {left !== undefined && (
                            <span className={`sf-now__budget${left < 60_000 ? ' sf-now__budget--low' : ''}`}>
                                <FontAwesomeIcon icon="clock" /> gives up at {runTime(scenario.waitingUntilUtc)}, in{' '}
                                {formatDuration(left)}
                            </span>
                        )}
                    </span>
                </span>
            </span>
        </div>
    );
};
